import { BaseApiServiceImpl } from "./BaseApiServiceImpl";

const CACHE_PREFIX = "netDetect_";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Static fallback mapping of phone prefixes to network names.
 * Used when the backend API is unavailable.
 */
const FALLBACK_PREFIXES: Record<string, string> = {
  // Uganda
  "25670": "AIRTEL", "25671": "AIRTEL", "25672": "AIRTEL",
  "25677": "MTN", "25678": "MTN", "25679": "MTN", "25676": "MTN",
  // Kenya
  "25470": "SAFARICOM", "25471": "SAFARICOM", "25472": "SAFARICOM",
  "25473": "AIRTEL", "25474": "SAFARICOM",
  // Ghana
  "23324": "MTN", "23325": "MTN", "23326": "MTN",
  "23320": "AIRTEL", "23327": "AIRTEL",
};

export interface NetworkDetectionResult {
  network: string;
  countryId: number | null;
  countryName: string | null;
}

function getCached(phone: string): NetworkDetectionResult | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + phone);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_PREFIX + phone);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setCache(phone: string, data: NetworkDetectionResult | null) {
  try {
    localStorage.setItem(CACHE_PREFIX + phone, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // storage full or unavailable — ignore
  }
}

function getFallback(phone: string): NetworkDetectionResult | null {
  const sanitized = phone.startsWith("+") ? phone.substring(1) : phone;
  if (sanitized.length < 5) return null;
  const code = sanitized.substring(0, 5);
  const network = FALLBACK_PREFIXES[code];
  if (!network) return null;
  return { network, countryId: null, countryName: null };
}

/**
 * Detect the mobile network provider for a phone number.
 * Tries: 1) cache, 2) backend API, 3) static fallback map.
 */
export async function detectNetwork(phone: string): Promise<NetworkDetectionResult | null> {
  const cleaned = phone.replace(/[^0-9+]/g, "");
  if (cleaned.length < 6) return null;

  const cached = getCached(cleaned);
  if (cached) return cached;

  try {
    const response: any = await new BaseApiServiceImpl("/api/v1/lookups/detect-network")
      .getRequestWithJsonResponse({ phone: cleaned });
    const data = response?.data;
    if (data && data.network) {
      const result: NetworkDetectionResult = {
        network: data.network,
        countryId: data.countryId ?? null,
        countryName: data.countryName ?? null,
      };
      setCache(cleaned, result);
      return result;
    }
  } catch {
    // API failed — fall through to static fallback
  }

  const fallback = getFallback(cleaned);
  setCache(cleaned, fallback);
  return fallback;
}
